import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle2, Activity, Leaf, Heart, Sparkles } from 'lucide-react';
import { thematicContent } from '../data/thematicContent';
import RelatedPages from '../components/RelatedPages';
import { relatedPagesMap } from '../data/relatedPages';
import FAQ from '../components/FAQ';
import { faqMap } from '../data/faqContent';
import SEO from '../components/SEO';

const NutritionHormonale = () => {
  const pageData = thematicContent.nutritionHormonale;

  return (
    <div style={{ marginTop: '100px' }}>
      <SEO
        title={pageData.meta.title}
        description={pageData.meta.description}
        keywords={pageData.meta.keywords}
        url="https://nutricentreriviera.ch/nutrition-hormonale"
      />
      {/* Hero Section */}
      <section style={{
        background: 'var(--gradient-hero)',
        padding: '4rem 0',
        textAlign: 'center'
      }}>
        <div className="container">
          <h1 className="heading-hero" style={{ marginBottom: '1.5rem' }}>
            {pageData.h1}
          </h1>
          <p className="heading-subtitle" style={{ maxWidth: '900px', margin: '0 auto 2rem' }}>
            {pageData.intro}
          </p>
          <a href="https://www.onedoc.ch/fr/therapeute-en-nutrition-mco/vevey/pcyv4/zara-nawid" target="_blank" rel="noopener noreferrer" className="btn-primary">
            Prendre rendez-vous
            <ArrowRight size={20} />
          </a>
        </div>
      </section>

      {/* Main Content */}
      <section className="section" style={{ background: 'white' }}>
        <div className="container" style={{ maxWidth: '900px' }}>
          {/* Section 1 */}
          <div style={{ marginBottom: '4rem' }}>
            <h2 className="heading-2" style={{ marginBottom: '1.5rem' }}>
              {pageData.sections[0].title}
            </h2>
            <p className="body-large">
              {pageData.sections[0].content}
            </p>
          </div>

          {/* Section 2 - List */}
          <div style={{ marginBottom: '4rem' }}>
            <h2 className="heading-2" style={{ marginBottom: '1.5rem' }}>
              {pageData.sections[1].title}
            </h2>
            <div style={{
              background: 'var(--bg-page)',
              borderRadius: '1rem',
              padding: '2rem',
              border: '1px solid var(--border-light)'
            }}>
              <p className="body-large" style={{ marginBottom: '1.5rem' }}>
                Les déséquilibres hormonaux peuvent se manifester par :
              </p>
              <ul style={{
                listStyle: 'none',
                padding: 0,
                display: 'flex',
                flexDirection: 'column',
                gap: '1rem'
              }}>
                {pageData.sections[1].list.map((item, index) => (
                  <li key={index} style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '1rem'
                  }}>
                    <CheckCircle2 size={24} style={{ 
                      color: 'var(--accent-primary)', 
                      flexShrink: 0,
                      marginTop: '0.2rem'
                    }} />
                    <span className="body-medium">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Section 3 - Notre approche */}
          <div style={{ marginBottom: '4rem' }}>
            <h2 className="heading-2" style={{ marginBottom: '1.5rem' }}>
              {pageData.sections[2].title}
            </h2>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
              gap: '2rem'
            }}>
              {pageData.sections[2].subsections.map((sub, index) => (
                <div key={index} style={{
                  background: 'white',
                  borderRadius: '1rem',
                  padding: '2rem',
                  border: '1px solid var(--border-light)',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
                  transition: 'all 0.3s ease'
                }} className="approach-card">
                  <div style={{
                    width: '3rem',
                    height: '3rem',
                    borderRadius: '0.75rem',
                    background: 'var(--accent-primary-light)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginBottom: '1rem',
                    color: 'var(--accent-primary)'
                  }}>
                    {index === 0 && <Activity size={24} />}
                    {index === 1 && <Leaf size={24} />}
                    {index === 2 && <Heart size={24} />}
                  </div>
                  <h3 className="heading-3" style={{ marginBottom: '0.75rem' }}>
                    {sub.subtitle}
                  </h3>
                  <p className="body-medium">
                    {sub.text}
                  </p>
                </div>
              ))}
            </div>
          </div>

          {/* Section 4 - Les piliers */}
          <div style={{ marginBottom: '4rem' }}>
            <h2 className="heading-2" style={{ marginBottom: '1.5rem' }}>
              {pageData.sections[3].title}
            </h2>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
              {pageData.sections[3].pillars.map((pillar, index) => (
                <div key={index} style={{
                  background: 'var(--bg-page)',
                  borderRadius: '1rem',
                  padding: '1.5rem',
                  borderLeft: '4px solid var(--accent-primary)'
                }}>
                  <h3 className="heading-3" style={{ marginBottom: '0.5rem' }}>
                    {pillar.name}
                  </h3>
                  <p className="body-medium">
                    {pillar.description}
                  </p>
                </div>
              ))}
            </div>
          </div>

          {/* Section 5 - Pour qui */}
          <div style={{ marginBottom: '4rem' }}>
            <h2 className="heading-2" style={{ marginBottom: '1.5rem' }}>
              {pageData.sections[4].title}
            </h2>
            <p className="body-large" style={{ marginBottom: '1.5rem' }}>
              La nutrition hormonale est particulièrement recommandée si vous souffrez de :
            </p>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
              gap: '1rem'
            }}>
              {pageData.sections[4].conditions.map((condition, index) => (
                <div key={index} style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.75rem',
                  padding: '1rem',
                  background: 'white',
                  borderRadius: '0.75rem',
                  border: '1px solid var(--border-light)'
                }}>
                  <Sparkles size={20} style={{ color: 'var(--accent-primary)', flexShrink: 0 }} />
                  <span className="body-medium">{condition}</span>
                </div>
              ))}
            </div>
          </div>

          <style>{`
            .approach-card:hover {
              transform: translateY(-4px);
              box-shadow: 0 8px 24px rgba(0,0,0,0.08);
            }
          `}</style>
        </div>
      </section>

      <FAQ items={faqMap['nutrition-hormonale']} />

      <RelatedPages items={relatedPagesMap['nutrition-hormonale']} />

      {/* CTA Section */}
      <section style={{
        background: 'var(--accent-primary)',
        color: 'white',
        padding: '5rem 0'
      }}>
        <div className="container" style={{ textAlign: 'center' }}>
          <h2 className="heading-1" style={{ color: 'white', marginBottom: '1rem' }}>
            {pageData.cta.title}
          </h2>
          <p className="body-large" style={{ 
            color: 'white', 
            opacity: 0.95, 
            maxWidth: '700px', 
            margin: '0 auto 2.5rem' 
          }}>
            {pageData.cta.text}
          </p>
          <a 
            href="https://www.onedoc.ch/fr/therapeute-en-nutrition-mco/vevey/pcyv4/zara-nawid" target="_blank" rel="noopener noreferrer" 
            className="btn-primary" 
            style={{ 
              background: 'white', 
              color: 'var(--accent-primary)',
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)'
            }}
          >
            {pageData.cta.button}
            <ArrowRight size={20} />
          </a>
          <p className="body-small" style={{ marginTop: '1.5rem', color: 'white', opacity: 0.9 }}>
            Centre Harmonie Féminine - Vevey, Montreux, Riviera vaudoise
          </p>
        </div>
      </section>
    </div>
  );
};

export default NutritionHormonale;
